import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { Callout, H2, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('how-to-start-beatmaking'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        Beatmaking has the lowest gear barrier of any music discipline in 2026. You need a laptop, a
        free DAW, and a pair of headphones. That is it. The rest is hours of practice and finishing
        beats.
      </P>
      <P>
        This is the complete roadmap from zero to your first finished beat - and from finished beats
        to actually getting better at making them.
      </P>

      <H2 id="step-1">Step 1 - Gear</H2>
      <UL>
        <li>
          <strong>Laptop or desktop computer</strong>: any machine made in the last 5 years works. 8
          GB RAM minimum, 16 GB ideal.
        </li>
        <li>
          <strong>Headphones</strong>: any over-ear pair you already own. Studio monitors are not
          required to start.
        </li>
        <li>
          <strong>Audio interface</strong>: optional. Skip until you start recording vocals or
          instruments.
        </li>
        <li>
          <strong>MIDI controller</strong>: optional. A 25-key USB keyboard makes melody-writing 5x
          faster but is not required.
        </li>
      </UL>
      <Callout>
        Do not buy gear before you have shipped 10 beats. Use what you have. Most producers in the
        top 100 leaderboards started on a laptop and stock headphones.
      </Callout>

      <H2 id="step-2">Step 2 - Pick a DAW</H2>
      <P>Free options that can take you all the way to professional release:</P>
      <UL>
        <li>
          <strong>Mac</strong>: GarageBand (built-in, free, upgrade path to Logic Pro)
        </li>
        <li>
          <strong>Windows</strong>: Cakewalk by BandLab (full free DAW) or FL Studio trial
        </li>
        <li>
          <strong>Browser</strong>: BandLab Web or Soundation
        </li>
      </UL>
      <P>
        See our full breakdown:{' '}
        <Link
          to="/blog/$slug"
          params={{ slug: 'best-free-daws-for-beatmakers-2026' }}
          className="underline decoration-primary/50 underline-offset-2"
        >
          The 14 best free DAWs for beatmakers in 2026
        </Link>
        .
      </P>

      <H2 id="step-3">Step 3 - Pick a genre and BPM</H2>
      <P>
        Pick the genre you listen to the most. You will know what good sounds like in that genre,
        and that knowledge is the most valuable thing a new beatmaker has. Start at the BPM range
        typical for that genre:
      </P>
      <UL>
        <li>Trap: 130-160 BPM</li>
        <li>Hip-hop: 80-95 BPM</li>
        <li>House: 120-128 BPM</li>
        <li>Drum and bass: 170-176 BPM</li>
        <li>Lo-fi: 70-90 BPM</li>
        <li>Phonk: 130-150 BPM</li>
      </UL>

      <H2 id="step-4">Step 4 - Lay drums first</H2>
      <P>
        Open your DAW. Open the drum sampler. Lay a kick on every downbeat (1, 2, 3, 4) and a snare
        on 2 and 4. Add a hi-hat on every 8th note. That is the universal starting groove. Loop it
        until it feels good.
      </P>

      <H2 id="step-5">Step 5 - Add bass</H2>
      <P>
        Pick a key. C minor is the safest starting point. Lay a single sub-bass note on the root of
        the key. Lock it to your kick on beats 1 and 3. Move it around to add interest.
      </P>

      <H2 id="step-6">Step 6 - Add a melody</H2>
      <P>
        Pick a melodic instrument from your DAW's stock library: bell, pluck, piano, pad,
        synth-lead. Write a 4-bar loop in the same key as the bass. Two rules: stay in scale, keep
        it sparse (4-8 notes per bar).
      </P>

      <H2 id="step-7">Step 7 - Arrange (intro/drop/breakdown/drop)</H2>
      <OL>
        <li>0-8s: melody only, drums coming in</li>
        <li>8-32s: full drop (everything together)</li>
        <li>32-48s: breakdown - kick out, melody alone</li>
        <li>48-80s: drop again with a new layer</li>
        <li>80-90s: outro tail</li>
      </OL>

      <H2 id="step-8">Step 8 - Mix and export</H2>
      <UL>
        <li>Lower all tracks until the master peaks at -6 dB.</li>
        <li>Stock limiter on master, ceiling -1 dB, gain reduction ~4 dB.</li>
        <li>Export as WAV or 320kbps MP3.</li>
      </UL>

      <H2 id="step-9">Step 9 - Ship it</H2>
      <P>
        Now the most important step: get the beat in front of ears. The fastest way is to enter a
        beat battle. Sign up free at{' '}
        <Link to="/" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>
        . Pick a quickplay match in your genre. Submit. The audience scores it blind, you find out
        exactly how it lands against five other producers using the same kit, and you walk away with
        concrete data on what to fix.
      </P>
      <Pull>
        You learn 10x faster shipping bad beats publicly than perfecting good beats privately.
      </Pull>

      <H2 id="path-forward">The path forward</H2>
      <OL>
        <li>Make 10 beats in your first month. Quantity, not quality.</li>
        <li>Ship every one of them - to a battle, SoundCloud, or a friend.</li>
        <li>
          Pick one weak area to focus on each month: drums, mixing, melody, arrangement. Watch one
          tutorial. Do 5 beats focused on that area.
        </li>
        <li>By month 3, your beats will be better than 80% of beginners. That is the curve.</li>
      </OL>
      <P>
        Beatmaking is unusually rewarding to put time into. The skill curve is steep at the start
        and the feedback loop on a battle platform is fast. Pick your DAW, make your first beat
        tonight, submit it tomorrow.
      </P>
    </>
  );
}
